import {afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, Injector, linkedSignal, viewChild} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NgTemplateOutlet} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {interval} from 'rxjs';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {IntercomService} from 'src/app/services/ui/intercom.service';
import {trialMinutesLeft} from 'src/app/utils/tenant-plan.utils';

/** Every trial counts down from the engine's default length (the start of a trial is not stored). */
const TRIAL_LENGTH_DAYS = 30;

/** What the badge prints of the minutes left, rounded up: days, then hours on the trial's last day, then minutes on its last hour. */
export function trialCountdown(minutesLeft: number) {
	if (minutesLeft < 60) {
		return {count: minutesLeft, key: minutesLeft > 1 ? 'menu.trial.minutesLeft' : 'menu.trial.minuteLeft', shortKey: 'menu.trial.minutesLeftShort'};
	}
	const hoursLeft = Math.ceil(minutesLeft / 60);
	if (hoursLeft <= 24) {
		return {count: hoursLeft, key: hoursLeft > 1 ? 'menu.trial.hoursLeft' : 'menu.trial.hourLeft', shortKey: 'menu.trial.hoursLeftShort'};
	}
	return {count: Math.ceil(hoursLeft / 24), key: 'menu.trial.daysLeft', shortKey: 'menu.trial.daysLeftShort'};
}

/**
 * KAB-2399 — Header badge of a workspace on trial: time left, unfolding into a panel that points to
 * the sales team (Figma Ready 16538:30492 folded, 16538:30940 unfolded).
 */
@Component({
	selector: 'app-trial-badge',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NgTemplateOutlet, TranslateModule],
	templateUrl: './trial-badge.component.html',
	styleUrl: './trial-badge.component.scss',
	host: {
		'(document:click)': 'closeOnOutsideClick($event)',
		'(document:keydown.escape)': 'expanded() && close()'
	}
})
export class TrialBadgeComponent {

	private host = inject<ElementRef<HTMLElement>>(ElementRef);
	private injector = inject(Injector);
	private translate = inject(TranslateService);
	private intercomService = inject(IntercomService);
	private tenantStore = inject(CurrentTenantStore);

	private readonly pill = viewChild<ElementRef<HTMLButtonElement>>('pill');
	private readonly panelHeader = viewChild<ElementRef<HTMLButtonElement>>('panelHeader');

	/** Ticks every minute: on its last hour the trial counts down in minutes. */
	private readonly minuteTick = toSignal(interval(60_000), {initialValue: 0});
	/** Re-read on every tick (the util reads the clock), not only when the tenant loads. */
	private readonly minutesLeft = computed(() => {
		this.minuteTick();
		return trialMinutesLeft(this.tenantStore.currentTenant());
	});
	private readonly daysLeft = computed(() => Math.ceil((this.minutesLeft() ?? 0) / (24 * 60)));

	protected readonly countdown = computed(() => {
		const minutesLeft = this.minutesLeft();
		return minutesLeft === undefined ? undefined : trialCountdown(minutesLeft);
	});
	/** Closed again on every workspace change — browser history can switch it while the panel is open. */
	protected readonly expanded = linkedSignal({source: this.tenantStore.currentTenantId, computation: () => false});

	/** Share of the trial already spent, in whole days: empty on its first day, full on its last (empty too on a trial extended past 30 days). */
	protected readonly elapsedPercent = computed(() => Math.max(0, 100 * (TRIAL_LENGTH_DAYS - this.daysLeft()) / (TRIAL_LENGTH_DAYS - 1)));

	/** The face follows the time left: content above 20 days, neutral down to 10, frowning below. */
	protected readonly moodIcon = computed(() => {
		const days = this.daysLeft();
		const mood = days > 20 ? 'smile' : days >= 10 ? 'neutral' : 'frown';
		return `assets/icons/custom-fields-icons-big/face-${mood}.svg`;
	});

	// The pill goes invisible under the panel: keep the keyboard focus on whichever header is shown.
	protected open(): void {
		this.expanded.set(true);
		afterNextRender(() => this.panelHeader()?.nativeElement.focus(), {injector: this.injector});
	}

	protected close(): void {
		this.expanded.set(false);
		afterNextRender(() => this.pill()?.nativeElement.focus(), {injector: this.injector});
	}

	protected contactSales(): void {
		this.expanded.set(false);
		this.intercomService.showNewMessage(this.translate.instant('menu.trial.salesMessage', {tenant: this.tenantStore.currentTenant()?.configuration.name}));
	}

	protected closeOnOutsideClick(event: MouseEvent): void {
		if (this.expanded() && !this.host.nativeElement.contains(event.target as Node)) {
			this.expanded.set(false);
		}
	}
}
